import React, { Fragment, useEffect, useState } from 'react';
import Head from 'next/head';
import type { GetStaticPathsResult, GetStaticPropsContext } from 'next';
import { fetchFromServer, fetchFromClient } from '../../../fetch';
import Header from '@/components/Header/Header';
import Footer from '@/components/Footer/Footer';
import Article from '@/components/Article/Article';
import { PATH_ARTICLES, PATH_AUTHORS } from '@/constants/spa-routes';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/constants/colors';
import { SmallArrowIcon } from '@/assets/icons/smallArrowIcon';
import { BlackButton } from '@/components/ButtonsCollection/BlackButton/BlackButton';
import Script from 'next/script';

type Articles = {
  id: string;
  attributes: {
    Title: string;
    Content: [];
    Hero_Image: {
      data: {
        attributes: {
          url: string;
          alternativeText: string;
        };
      };
    };
    Short_Description: string;
    publishedAt: Date;
    URL: string;
    author: {
      data: {
        attributes: {
          Name: string;
          Position: string;
          Avatar: {
            data: {
              attributes: {
                url: string;
              };
            };
          };
        };
      };
    };
    tags: {
      data: [
        {
          id: number;
          attributes: {
            Name: string;
          };
        },
      ];
    };
  };
};
type Author = {
  id: number;
  attributes: {
    Name: string;
    Position: string;
    articles: {
      data: Articles[];
    };
    Avatar: {
      data: {
        attributes: {
          url: string;
        };
      };
    };
  };
};
type HomeProps = {
  author: Author;
  meta: {
    pagination: {
      page: number;
      pageCount: number;
    };
  };
  error?: Error;
  originUrl: string;
};

export default function Home({ author, meta, error, originUrl }: HomeProps) {
  const initialPage = 1;
  const initialIsLastPage =
    meta && meta.pagination && meta.pagination.page === meta.pagination.pageCount;
  const authorName = author?.attributes?.Name || '';
  const authorPosition = author?.attributes?.Position || '';
  const [page, setPage] = useState(initialPage);
  const [posts, setPosts] = useState<Articles[] | []>([]);
  const [isLastPage, setIsLastPage] = useState(initialIsLastPage);
  const authorUrl = authorName.toLowerCase().trim().replace(/\ /g, '-');
  async function loadMore() {
    setPage(page + 1);
  }

  useEffect(() => {
    if (page === initialPage) {
      return;
    }
    (async () => {
      const articlesVar = await fetchFromClient(
        'GET',
        `/articles?sort=publishedAt:desc&pagination[page]=${page}&pagination[pageSize]=6&populate=Hero_Image,IMAGES_DESKTOP,IMAGES_MOBILE,tags.Name,views&filters[author][Name][$eqi]=${authorName}`
      );

      setPosts([...posts, ...articlesVar.data]);
      setIsLastPage(articlesVar.meta.pagination.page === articlesVar.meta.pagination.pageCount);
    })();
  }, [page]);

  useEffect(() => {
    let articles = author?.attributes?.articles?.data || [];
    articles = articles.sort((first, second) => {
      return (
        new Date(second?.attributes.publishedAt).valueOf() -
        new Date(first?.attributes.publishedAt).valueOf()
      );
    });
    if (articles.length > 6) {
      articles.length = 6;
      setPage(initialPage);
      setIsLastPage(false);
    }

    setPosts(articles);
  }, []);

  const sameArticles = author?.attributes?.articles.data.map((el) => {
    return `${originUrl}${el.attributes.URL}`;
  });

  if (error || !authorName) {
    return (
      <Fragment>
        <h1>Something went wrong</h1>
        <pre>{error?.message}</pre>
      </Fragment>
    );
  }
  return (
    <>
      <Head>
        <title>{`Insights from ${authorName}. CoSupport AI\`s ${authorPosition}`}</title>
        <meta
          key="subject"
          name="subject"
          content={`${authorName} on Generative AI for Customer Support as ${authorPosition}`}
        ></meta>
        <meta
          key="description"
          name="description"
          content={`Read insights and expert analysis from ${authorName} a leading' ${authorPosition} in the field of customer support and AI.`}
        />
        <link
          key="canonical"
          rel="canonical"
          href={`${originUrl}${PATH_AUTHORS.ROOT}/${authorUrl}`}
        />
        <meta
          key="ogtitle"
          property="og:title"
          content={`Insights from ${authorName}. CoSupport AI\`s ${authorPosition}`}
        ></meta>
        <meta key="ogtype" property="og:type" content="website" />
        <meta key="oglocale" property="og:locale" content="en_US" />
        <meta
          key="ogurl"
          property="og:url"
          content={`${originUrl}${PATH_AUTHORS.ROOT}/${authorUrl}`}
        />
        <meta
          key="ogimage"
          property="og:image"
          content={`${author?.attributes?.Avatar?.data?.attributes.url}`}
        />
        <meta key="ogsitename" property="og:site_name" content={`${originUrl}/`} />
        <meta
          key="ogdescription"
          property="og:description"
          content={`Discover the latest insights and innovations in generative AI for customer support from ${authorName}, a renowned ${authorPosition}. Learn how AI can transform customer interactions, improve efficiency, and provide valuable business intelligence for your company.`}
        />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <script
          key="person-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `{
                "@context": "https://schema.org",
                "@type": "Person",
                "name": "${authorName}",
                "image": "${author?.attributes?.Avatar?.data?.attributes?.url}",
                "description": "${author?.attributes.Position}",
                "sameAs": "${!!sameArticles?.length ? sameArticles : []}"
              }`,
          }}
        />
        <Script
          id="googletagmanager3"
          key="googletagmanager3"
          strategy="beforeInteractive"
          src="https://www.googletagmanager.com/gtag/js?id=AW-11237889467"
        />
        <Script
          id="googletagmanager4"
          key="googletagmanager4"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-11237889467');
          `,
          }}
        />
        <Script
          id="googletagmanager5"
          key="googletagmanager5"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
            (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', 'GTM-WXHFTKL9');
          `,
          }}
        />
        <Script
          id="dealfront"
          key="dealfront"
          dangerouslySetInnerHTML={{
            __html: `(function(ss,ex){ window.ldfdr=window.ldfdr||function(){(ldfdr._q=ldfdr._q||[]).push([].slice.call(arguments));}; (function(d,s){ fs=d.getElementsByTagName(s)[0]; function ce(src){ var cs=d.createElement(s); cs.src=src; cs.async=1; fs.parentNode.insertBefore(cs,fs); }; ce('https://sc.lfeeder.com/lftracker_v1_'+ss+(ex?'_'+ex:'')+'.js'); })(document,'script'); })('JMvZ8gnv0VPa2pOd');`,
          }}
        />
        <Script
          id="apollo"
          key="apollo"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `!function(){var e,a;e=Math.random().toString(36).substring(7),(a=document.createElement("script")).src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+e,a.async=!0,a.defer=!0,a.onload=function(){window.trackingFunctions.onLoad({appId:"66bb4c503be771078edff8ab"})},document.head.appendChild(a)}();`,
          }}
        />
      </Head>
      <Header pathname={`${PATH_ARTICLES.ROOT}/${authorUrl}`} />
      <main>
        <div className={'articles-title'}>
          <div className="article-author">
            <div className="article-author-avatar">
              <img
                src={author?.attributes?.Avatar?.data?.attributes?.url}
                width="100px"
                style={{ borderRadius: '50%' }}
              />
            </div>
            <div>
              <Typography
                as={'h1'}
                $type="Heading1"
                $fontSizeSm={'1.25vw'}
                $fontSizeXs={'0.875rem'}
                $lineHeight={{ xs: '170%' }}
                className={`font-jamjuree !font-semibold text-ebony`}
              >
                {authorName} {authorPosition && `/ ${authorPosition}`}
              </Typography>
              <Typography
                $type="PRegular"
                $fontSizeSm={'1.25vw'}
                $fontSizeXs={'0.875rem'}
                $color={'rgba(5, 5, 13, 0.30);'}
                className={`font-jamjuree !font-semibold`}
              >
                Author’s articles:
              </Typography>
            </div>
          </div>
        </div>
        <div className={'articles'}>
          {posts.map((article, index) => {
            // @ts-ignore
            return <Article key={article.id} article={article} />;
          })}
        </div>
        {isLastPage ? null : (
          <div className="load-more button-container">
            <BlackButton
              className="max-md:w-full"
              linkBtnClassName="max-md:w-full"
              onClick={() => loadMore()}
            >
              <div className="button-text flex justify-center items-center lg:gap-4-vw gap-4">
                <Typography $color={COLORS.WHITE} as="span" $type="PRegular" $weight="700">
                  Load more
                </Typography>
                <SmallArrowIcon color="green" />
              </div>
            </BlackButton>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  try {
    const data = await fetchFromServer('authors?fields[0]=Name');
    const authors: { attributes: { Name: string } }[] = data?.data || [];
    const paths = authors.map(({ attributes }) => ({
      params: {
        slug: `${attributes.Name.toLowerCase().trim().replace(/\ /g, '-')}`,
      },
    }));

    return {
      paths,
      fallback: 'blocking',
    };
  } catch (e) {
    return {
      paths: [],
      fallback: false,
    };
  }
}

export async function getStaticProps({ params = {} }: GetStaticPropsContext) {
  const originUrl: string = process.env.ORIGIN_URL || 'https://cosupport.ai';
  const { slug } = params;
  //@ts-ignore
  const name = slug?.replace(/\-/g, ' ')?.trim();

  try {
    let authors = await fetchFromServer(
      `authors?fields[0]=id&fields[1]=Name&fields[2]=Position&populate=Avatar,articles,articles.Hero_Image,articles.tags&filters[Name][$eqi]=${name}`
    );
    return {
      props: {
        author: authors.data[0],
        meta: authors.meta,
        originUrl,
      },
      revalidate: 60,
    };
  } catch (e) {
    return {
      props: {
        author: {},
        articles: [],
        error: { name: (e as Error).name, message: (e as Error).message },
        originUrl,
      },
      revalidate: 60,
    };
  }
}
